import { z } from 'zod'
import type { TransitionTable } from '../helpers/state-machine.js'

/**
 * What a person decided about an event. Mirrors the `EventLifecycle` enum in the
 * Prisma schema — the two must stay in sync.
 *
 * - `enquiry` — somebody asked. No date, no times, no lane held.
 * - `booked` — it is happening: a date and a window, holding its track.
 * - `cancelled` — it was let go. The record and its plans stay; the lane does not.
 * - `lost` — an inquiry that went nowhere. Kept, with a reason, so it counts in
 *   conversion rates; deleting is for mistakes. See `docs/funnel-stages.spec.md`.
 *
 * **Distinct from an event's *status*, which is derived.** Status adds `past`,
 * and `past` never appears here or in a history row because nobody performs it —
 * it happens to you. See `docs/event-scheduling.md`:
 *
 * ```
 * Event.lifecycle   inquiry | booked | canceled | lost          -- stored: a decision
 * derived status    inquiry | booked | past | canceled | lost   -- presentation only
 * ```
 *
 * This does not contradict `event-list.input.ts`, which argues status must be
 * derived: that argument is about a *date comparison against now* going stale
 * between being written and being read. It covers `past` and nothing else.
 *
 * The lifecycle names the dates rather than duplicating them, and the mapping is
 * not symmetrical: `enquiry` and `lost` are exactly "no date and no times", while
 * **both** `booked` and `cancelled` carry a date and a window — a cancellation is
 * a booking that was let go, not a booking erased. So an event has a date if and
 * only if it is booked or canceled. A lost inquiry never held a date, so it has
 * none to keep.
 */
export const EVENT_LIFECYCLES = ['enquiry', 'booked', 'cancelled', 'lost'] as const

export const eventLifecycleSchema = z.enum(EVENT_LIFECYCLES)

export type EventLifecycle = z.infer<typeof eventLifecycleSchema>

/**
 * The moves an event may make.
 *
 * Three absences are as load-bearing as the entries:
 *
 * - **`booked → enquiry` is missing.** That is "a dated event cannot be
 *   un-dated", declared rather than written as an `if` in a service.
 * - **`enquiry → cancelled` is missing.** An inquiry holds no lane and releases
 *   nothing, so there is no booking to cancel. One that goes nowhere is marked
 *   `lost`, with a reason; deleting is for mistakes (spam, duplicates, tests).
 * - **`lost → booked` is missing.** A lost inquiry is reopened first, back to
 *   `enquiry`, and then booked like any other: the couple came back.
 * - **`cancelled` is not terminal.** A canceled event may be restored, and
 *   *even after its date has passed* — that is correcting a record, not
 *   reopening a plan, and it comes back already frozen. Canceling a
 *   forward-looking booking and correcting a historical one are different acts;
 *   only the first needs a deadline.
 *
 * The guards that cannot be expressed as a table — "only while the event has not
 * finished", "the lane must still be free" — live with the transition helper,
 * which is where the event and the clock are both in hand.
 */
export const EVENT_LIFECYCLE_TRANSITIONS: TransitionTable<EventLifecycle> = {
  enquiry: ['booked', 'lost'],
  booked: ['cancelled'],
  cancelled: ['booked'],
  lost: ['enquiry'],
}

/** Lifecycles still in play. Every rule that means "not canceled" means this. */
export const LIVE_EVENT_LIFECYCLES = ['enquiry', 'booked'] as const satisfies readonly EventLifecycle[]

/** Whether an event is still in play: an inquiry or a booking. */
export function isLiveLifecycle(lifecycle: EventLifecycle): boolean {
  return LIVE_EVENT_LIFECYCLES.some((live) => live === lifecycle)
}

/**
 * An event's status, which is its lifecycle plus what the clock did.
 *
 * `past` is not a lifecycle: it is a comparison, and storing it would be stale
 * the moment it was written.
 */
export const EVENT_STATUSES = ['enquiry', 'booked', 'past', 'cancelled', 'lost'] as const

export const eventStatusSchema = z.enum(EVENT_STATUSES)

export type EventStatus = z.infer<typeof eventStatusSchema>
